import { InfoOutline } from "@mui/icons-material";
import { Box, Button, Card, CardContent, Link, Typography, Tooltip, IconButton } from "@mui/material";
import { useState } from "react";
import { format } from 'date-fns';
import { styled } from "@mui/material/styles";
import { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "../common/SnackbarContext";

interface AddNewRulesProps {
  onUpload: () => Promise<boolean>;
}

const UploadPayroll: React.FC<AddNewRulesProps> = ({ onUpload }) => {
  
  const { showSnackbar } = useSnackbar();
  
  const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
      color: theme.palette.common.black,
    },
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.common.black,
      maxWidth: 221,
      padding: "8px 12px",
      color: "#BFC1C2",
      fontSize: '12px',
      fontWeight: 400,
      fontFamily: 'Inter',
      border: '1px solid #343333'
    },
  }));

  const [filesInfo, setFilesInfo] = useState([
    { name: '', date: '' },
    { name: '', date: '' },
    { name: '', date: '' }
  ]);

   
  const handleInputClick = (index:number) =>(event:any) => {
    // Reset the input value to allow selecting the same file
    event.target.value = null; 
  };

  const [showSuccess, setShowSuccess] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<{ payroll?: File; time?: File }>({});
  const navigate = useNavigate();

  const handleOnChange = (index: number, type: "payroll" | "time") =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];

       if(undefined == file) {
             const newFilesInfo = [...filesInfo];
              newFilesInfo[index] = {
                name: "",
                date: ""
              };
            setFilesInfo(newFilesInfo);
      }

      if (file) {
        if(filesInfo[index].name != "") {
          showSnackbar(`Previous file was replaced by ${file.name}`,"success");
        }
        const newFilesInfo = [...filesInfo];
        newFilesInfo[index] = {
          name: file.name,
          date: format(new Date(), 'yyyy-MM-dd h:mm a')
        };
        setFilesInfo(newFilesInfo);
        setSelectedFiles(prev => ({ ...prev, [type]: file }));
      }
    }

  return (
    <div className="rules-upload-div">
      <div className="upload-block">
        <label className="div-txt-heading">Upload Payroll Files</label>
        <label className="div-txt-sub-heading">
          Upload payroll variance data and any other required supplementary data for payroll validation
        </label>
        <div>
          <Card className="upload-card" sx={{ borderRadius: '16px' }}>
            <CardContent sx={{ padding: '12px 20px !important', display: "flex" }}>
              <img src="/icons/payrollvariance.svg" alt="Rules" className="upload-card-icon" />
              <div className="content-div-upload flex-3">
                <div className="flex space-between ">
                  <div className="flex ">
                    <label className="upload-card-heading"> Payroll Variance Data <span className="required-field">*</span> </label>
                    {/* <InfoOutline className="upload-card-info" /> */}
                    <BootstrapTooltip title="Upload the payroll variance file with employee, wage and payroll details. (Sample file provided for reference)">
                      <IconButton
                        color="primary"
                        size="small" sx={{pt:0}}
                      ><InfoOutline className="upload-card-info" />
                      </IconButton>
                    </BootstrapTooltip>
                  </div>
                  <Link href="/sample-data/Payroll_Variance_Sample.xlsx" underline="always" className="sample-link">
                    Sample Data
                  </Link>
                </div>
                <div>
                  <Typography variant="body2" className="upload-card-upload-dt">
                    Last Uploaded : {filesInfo[0].date || '--'}
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <Button
                      component="label"
                      variant="outlined"
                      sx={{
                        borderStyle: "dashed",
                        borderColor: "#007CB0",
                        color: "#007CB0",
                        textTransform: 'none',
                        marginRight: '13px'
                      }}
                    >
                      Choose file
                      <input
                        type="file"
                        accept=".csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        id="rulespriorities"
                        name="rulespriorities"
                        hidden
                        onChange={handleOnChange(0, "payroll")}
                        onClick={handleInputClick(0)}
                      />
                    </Button>
                    <Typography sx={{ color: '#BDBDBD', fontStyle: 'italic', fontSize: '13px', fontWeight: '400' }}>
                      {filesInfo[0].name || "No File Chosen"}
                    </Typography>
                  </Box>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="upload-card" sx={{ borderRadius: '16px' }}>
            <CardContent sx={{ padding: '12px 20px !important', display: "flex" }}>
              <img src="/icons/timedatafile.svg" alt="wage_type" className="upload-card-icon" />
              <div className="content-div-upload flex-3">
                <div className="flex space-between ">
                  <div className="flex ">
                    <label className="upload-card-heading"> Time Data File </label>
                    {/* <InfoOutline className="upload-card-info" /> */}
                    <BootstrapTooltip title="Upload the time variance file, if applicable, with work hours and overtime details. (Sample file provided for reference)">
                      <IconButton
                        color="primary"
                        size="small" sx={{pt:0}}
                      ><InfoOutline className="upload-card-info" />
                      </IconButton>
                    </BootstrapTooltip>
                  </div>
                  <Link href="/sample-data/Time_Data_Sample.xlsx" underline="always" className="sample-link">
                    Sample Data
                  </Link>
                </div>
                <div>
                  <Typography variant="body2" className="upload-card-upload-dt">
                    Last Uploaded : {filesInfo[1].date || '--'}
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <Button
                      component="label"
                      variant="outlined"
                      sx={{
                        borderStyle: "dashed",
                        borderColor: "#007CB0",
                        color: "#007CB0",
                        textTransform: 'none',
                        marginRight: '13px'
                      }}
                    >
                      Choose file
                      <input
                        type="file"
                        accept=".csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        id="wagetype"
                        name="wagetype"
                        hidden
                        onChange={handleOnChange(1, "time")}
                        onClick={handleInputClick(1)}
                      />
                    </Button>
                    <Typography sx={{ color: '#BDBDBD', fontStyle: 'italic', fontSize: '13px', fontWeight: '400' }}>
                      {filesInfo[1].name || "No File Chosen"}
                    </Typography>
                  </Box>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="required-field" style={{ textAlign: 'right',paddingTop: '0.5em',fontSize:'13px', fontWeight:400}}>
                      * Mandatory upload
          </div >
        </div>
      </div>
    </div>
  );
};

export default UploadPayroll;
